import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  X,
  Pencil,
  Heart,
  Wrench,
  ShoppingCart,
  Palette,
  Globe,
  TrendingUp,
  ListOrdered,
  CalendarDays,
  Ban,
  KeyRound,
  Shield,
  Plus,
  Tag,
} from "lucide-react";
import { updateProfil, getUser } from "../../actions/user.actions";
import { getUsers } from "../../actions/users.actions";
import { getServices, createService } from "../../actions/service.actions";
import ImageProfil from "./ImageProfil";

// Map nom d'icône (stocké en base) -> composant lucide
const ICONS = {
  Heart,
  Wrench,
  ShoppingCart,
  Palette,
  Globe,
  TrendingUp,
  ListOrdered,
  CalendarDays,
  Tag,
};

// Liste de secours si les services ne sont pas encore chargés depuis l'API
const FALLBACK_SERVICES = [
  { value: "Coach", label: "Coach", icon: "Heart" },
  { value: "Référent technique", label: "Référent technique", icon: "Wrench" },
  { value: "E-commerce", label: "Chargé de site E-commerce", icon: "ShoppingCart" },
  { value: "Graphiste", label: "Graphiste", icon: "Palette" },
  { value: "Chargé(e) de projet web", label: "Chargé(e) de projet web", icon: "Globe" },
  { value: "Traffic manager", label: "Traffic manager", icon: "TrendingUp" },
  { value: "Ordonnancement", label: "Ordonnancement", icon: "ListOrdered" },
  { value: "Planification", label: "Planification", icon: "CalendarDays" },
];

const ROLES = [
  { key: "isAdmin", label: "Administrateur", icon: Shield },
  { key: "isCoach", label: "Coach", icon: Heart },
  { key: "isReferent", label: "Référent", icon: Wrench },
];

const EditProfilModal = ({ user, isSelf, canManage, onClose }) => {
  const [firstname, setFirstname] = useState(user.firstname || "");
  const [name, setName] = useState(user.name || "");
  const [email, setEmail] = useState(user.email || "");
  const [birthday, setBirthday] = useState(
    user.birthday ? user.birthday.slice(0, 10) : ""
  );
  const [service, setService] = useState(user.service || "");
  // Mode "Autre" : saisie libre d'un nouveau service (détecté ci-dessous au chargement)
  const [useCustomService, setUseCustomService] = useState(false);
  const [coach, setCoach] = useState(user.coach || "");
  const [roles, setRoles] = useState({
    isAdmin: !!user.isAdmin,
    isCoach: !!user.isCoach,
    isReferent: !!user.isReferent,
  });
  const [showPassword, setShowPassword] = useState(false);
  const [password, setPassword] = useState("");
  const [saving, setSaving] = useState(false);

  const dispatch = useDispatch();
  const usersData = useSelector((state) => state.usersReducer);
  const servicesData = useSelector((state) => state.serviceReducer);

  // Services chargés depuis l'API, avec repli sur la liste de secours
  const services = servicesData && servicesData.length
    ? servicesData
    : FALLBACK_SERVICES;

  // Coachs sélectionnables : service "Coach" + la Direction (service "Direction")
  const coaches = Object.values(usersData).filter(
    (u) =>
      (u.service === "Coach" || u.service === "Direction") &&
      u.id !== user.id
  );

  // Charge la liste des services au montage
  useEffect(() => {
    dispatch(getServices());
  }, [dispatch]);

  // Quand les services sont chargés, bascule en mode "Autre" si le service
  // du profil n'existe pas (cas d'un ancien service libre non persisté)
  useEffect(() => {
    if (
      servicesData &&
      servicesData.length &&
      user.service &&
      !servicesData.some((s) => s.value === user.service)
    ) {
      setUseCustomService(true);
    }
  }, [servicesData, user.service]);

  useEffect(() => {
    const onEscape = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onEscape);
    return () => document.removeEventListener("keydown", onEscape);
  }, [onClose]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const fields = { firstname, name, email, birthday };
    if (canManage) {
      let serviceValue = service.trim();
      if (useCustomService) {
        if (!serviceValue) {
          toast.error("Indique le nom du service personnalisé.");
          return;
        }
        // Persiste le nouveau service s'il n'existe pas déjà (réutilisable partout)
        const exists = services.some(
          (s) => s.value.toLowerCase() === serviceValue.toLowerCase()
        );
        if (!exists) {
          try {
            const created = await dispatch(createService(serviceValue));
            serviceValue = created?.value || serviceValue;
          } catch (err) {
            toast.error("La création du service a échoué.");
            return;
          }
        }
      }
      Object.assign(fields, { service: serviceValue, coach, ...roles });
    }
    if ((isSelf || canManage) && password.trim()) {
      if (password.trim().length < 8) {
        toast.error("Le mot de passe doit faire au moins 8 caractères.");
        return;
      }
      fields.password = password.trim();
    }
    setSaving(true);
    try {
      await dispatch(updateProfil(user.id, fields));
      await dispatch(getUsers());
      if (isSelf) await dispatch(getUser(user.id));
      toast.success("Profil mis à jour ✨");
      onClose();
    } catch (error) {
      toast.error("La mise à jour a échoué, réessaie.");
      setSaving(false);
    }
  };

  return createPortal(
    <div
      className="edit-profil-overlay"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <form className="edit-profil" onSubmit={handleSubmit}>
        <div className="edit-profil-header">
          <h3>
            <Pencil size={15} />
            {isSelf
              ? "Éditer mon profil"
              : `Éditer le profil de ${user.firstname}`}
          </h3>
          <button
            type="button"
            className="edit-profil-close"
            onClick={onClose}
            aria-label="Fermer"
          >
            <X size={15} />
          </button>
        </div>

        <div className="edit-profil-body">
          <div className="edit-row">
            <div className="edit-field">
              <label htmlFor="edit-firstname">Prénom</label>
              <input
                id="edit-firstname"
                type="text"
                value={firstname}
                onChange={(e) => setFirstname(e.target.value)}
                required
              />
            </div>
            <div className="edit-field">
              <label htmlFor="edit-name">Nom</label>
              <input
                id="edit-name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </div>
          </div>

          <div className="edit-row">
            <div className="edit-field grow">
              <label htmlFor="edit-email">Email</label>
              <input
                id="edit-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <div className="edit-field">
              <label htmlFor="edit-birthday">Entrée chez Local'</label>
              <input
                id="edit-birthday"
                type="date"
                value={birthday}
                onChange={(e) => setBirthday(e.target.value)}
                required
              />
            </div>
          </div>

          {canManage && (
          <>
          <div className="edit-field">
            <label>Service</label>
            <div className="service-chips">
              {services.map(({ value, label, icon }) => {
                const Icon = ICONS[icon] || Tag;
                return (
                  <button
                    type="button"
                    key={value}
                    className={`chip ${
                      !useCustomService && service === value ? "selected" : ""
                    }`}
                    onClick={() => {
                      setUseCustomService(false);
                      setService(value);
                    }}
                    title={label}
                  >
                    <Icon size={14} />
                    {label}
                  </button>
                );
              })}
              <button
                type="button"
                className={`chip ${useCustomService ? "selected" : ""}`}
                onClick={() => {
                  setUseCustomService(true);
                  // Si un service prédéfini était sélectionné, on repart d'un champ vide
                  if (services.some((s) => s.value === service)) setService("");
                }}
                title="Créer un service personnalisé"
              >
                <Plus size={14} />
                Autre
              </button>
            </div>
            {useCustomService && (
              <input
                type="text"
                className="service-custom-input"
                placeholder="Nom du service personnalisé"
                value={service}
                onChange={(e) => setService(e.target.value)}
                maxLength={255}
                autoFocus
              />
            )}
          </div>

          <div className="edit-field">
            <label>Coach</label>
            <div className="coach-cards">
              {coaches.map((c) => {
                const fullName = `${c.firstname} ${c.name}`;
                return (
                  <button
                    type="button"
                    key={c.id}
                    className={`coach-card ${coach === fullName ? "selected" : ""}`}
                    onClick={() => setCoach(fullName)}
                  >
                    <span className="avatar">
                      <span className="avatar-initials">
                        {(c.firstname?.[0] || "") + (c.name?.[0] || "")}
                      </span>
                      <ImageProfil id={c.id} />
                    </span>
                    <span className="coach-name">
                      {c.firstname} {c.name?.[0]}.
                    </span>
                  </button>
                );
              })}
              <button
                type="button"
                className={`coach-card none ${coach === "" ? "selected" : ""}`}
                onClick={() => setCoach("")}
              >
                <span className="avatar">
                  <Ban size={16} />
                </span>
                <span className="coach-name">Aucun</span>
              </button>
            </div>
          </div>
          </>
          )}

          {canManage && (
            <div className="edit-field">
              <label>Rôles</label>
              <div className="role-switches">
                {ROLES.map(({ key, label, icon: Icon }) => {
                  const lockedSelfAdmin = key === "isAdmin" && isSelf;
                  return (
                    <button
                      type="button"
                      key={key}
                      className={`role-switch ${roles[key] ? "on" : ""}`}
                      disabled={lockedSelfAdmin}
                      title={
                        lockedSelfAdmin
                          ? "Impossible de retirer son propre rôle admin"
                          : undefined
                      }
                      onClick={() =>
                        setRoles((r) => ({ ...r, [key]: !r[key] }))
                      }
                    >
                      <Icon size={14} />
                      {label}
                      <span className="switch-track" aria-hidden="true">
                        <span className="switch-knob"></span>
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {(isSelf || canManage) && (
            <div className="edit-field">
              {!showPassword ? (
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword(true)}
                >
                  <KeyRound size={14} />{" "}
                  {isSelf
                    ? "Changer mon mot de passe"
                    : "Définir un nouveau mot de passe"}
                </button>
              ) : (
                <>
                  <label htmlFor="edit-password">
                    Nouveau mot de passe{" "}
                    <em>
                      {isSelf
                        ? "(laisser vide pour ne pas changer)"
                        : "(8 caractères minimum, laisser vide pour ne pas changer)"}
                    </em>
                  </label>
                  <input
                    id="edit-password"
                    type="password"
                    autoComplete="new-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                  />
                </>
              )}
            </div>
          )}
        </div>

        <div className="edit-profil-footer">
          <button type="button" className="btn-cancel" onClick={onClose}>
            Annuler
          </button>
          <button type="submit" className="btn-save" disabled={saving}>
            {saving ? "Enregistrement…" : "Enregistrer"}
          </button>
        </div>
      </form>
    </div>,
    document.body
  );
};

export default EditProfilModal;
